import { type AppState, Auth0Provider } from "@auth0/auth0-react";
import { useNavigate, useLocation } from "react-router";
import type { ReactNode } from "react";
import { currentQaEnvironment } from "../qa-build-environment";

// Type for the location state
interface LocationState {
  returnTo?: string;
}

export function Auth0ProviderWithNavigate({
  children,
}: {
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const qa = currentQaEnvironment(window.location.origin);
  const ephemeralQa = !!qa && qa.environment.startsWith("qa-");

  const domain = import.meta.env.VITE_AUTH0_DOMAIN;
  const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
  const redirectUri = import.meta.env.VITE_AUTH0_CALLBACK_URL;
  const audience = import.meta.env.VITE_AUTH0_AUDIENCE;

  const onRedirectCallback = (appState: AppState | undefined) => {
    const locationState = location.state as LocationState | null;
    const returnTo =
      appState?.returnTo ?? locationState?.returnTo ?? "/dashboard";
    const safeReturn =
      qa &&
      (!returnTo.startsWith("/") ||
        returnTo.startsWith("//") ||
        returnTo.includes("\\"))
        ? "/dashboard"
        : returnTo;
    void navigate(safeReturn, { replace: true });
  };

  if (!(domain && clientId && redirectUri)) {
    return <>{children}</>;
  }

  // PKCE transaction state belongs to the origin that starts sign-in. Both
  // canonical and retained hosts are registered with Auth0; never cross origins
  // on the callback or the SDK cannot recover that transaction.
  const configuredCallback = new URL(redirectUri);
  const callbackPath =
    configuredCallback.pathname === "/" ? "" : configuredCallback.pathname;
  const callbackUri = `${window.location.origin}${callbackPath}${configuredCallback.search}`;

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: callbackUri,
        audience: audience,
        scope: "openid profile email",
      }}
      onRedirectCallback={onRedirectCallback}
      cacheLocation={ephemeralQa ? "memory" : "localstorage"}
      useRefreshTokens={!ephemeralQa}
    >
      {children}
    </Auth0Provider>
  );
}
